import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import 'styles/common.css';
import './RequestQuote.css';

const RequestQuote = () => {
  // TODO: replace with the deployed Apps Script Web App URL for Enterprise quote requests
  // (a dedicated deployment, separate from Contact.jsx's script — see implementation plan).
  const GOOGLE_APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycby31hO0cPuisLreSSvtAU2J-ZzKBlYkIHqmoQK_n5MJKGlZ-UgH1NLNTC1dMBpo0bfd/exec';

  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    projectDescription: '',
    budget: '',
    website: '' // honeypot: real users never fill this in
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.contactPerson.trim()) newErrors.contactPerson = 'Contact person is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.projectDescription.trim()) newErrors.projectDescription = 'Please describe your project';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.website) {
      // Honeypot tripped — silently pretend success without sending.
      setSubmitSuccess(true);
      return;
    }
    if (validateForm()) {
      setIsSubmitting(true);

      const formDetails = new URLSearchParams();
      Object.keys(formData).forEach(key => {
        formDetails.append(key, formData[key]);
      });

      try {
        await fetch(GOOGLE_APPS_SCRIPT_URL, {
          method: 'POST',
          mode: 'no-cors',
          body: formDetails,
        });

        // Using 'no-cors' means we cannot read the response — assume success
        // if the fetch promise resolves without throwing.
        setSubmitSuccess(true);
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          phone: '',
          projectDescription: '',
          budget: '',
          website: ''
        });
      } catch (error) {
        console.error('Error submitting quote request:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="quote-page">
      <div className="quote-container">
        <div className="quote-header">
          <Link to="/membership" className="back-button">← Back to Membership</Link>
          <h1 className="quote-title">Request an Enterprise Quote</h1>
          <p className="quote-subtitle">
            Tell us about your project and budget, and our team will follow up
            with a custom quote for your organization
          </p>
        </div>

        <div className="quote-form-card">
          <form onSubmit={handleSubmit} className="quote-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="companyName" className="form-label" required>
                  Company Name
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  autoComplete="off"
                  value={formData.companyName}
                  onChange={handleChange}
                  className={`form-input ${errors.companyName ? 'error' : ''}`}
                  placeholder="Enter your company name"
                />
                {errors.companyName && <span className="error-text">{errors.companyName}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="contactPerson" className="form-label" required>
                  Contact Person
                </label>
                <input
                  type="text"
                  id="contactPerson"
                  name="contactPerson"
                  autoComplete="off"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  className={`form-input ${errors.contactPerson ? 'error' : ''}`}
                  placeholder="Enter your full name"
                />
                {errors.contactPerson && <span className="error-text">{errors.contactPerson}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email" className="form-label" required>
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  placeholder="Enter your email"
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter your phone number"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="projectDescription" className="form-label" required>
                Project Description
              </label>
              <textarea
                id="projectDescription"
                name="projectDescription"
                value={formData.projectDescription}
                onChange={handleChange}
                className={`form-textarea ${errors.projectDescription ? 'error' : ''}`}
                placeholder="Tell us about your project, goals, and requirements..."
                rows="5"
              />
              {errors.projectDescription && <span className="error-text">{errors.projectDescription}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="budget" className="form-label">
                Budget
              </label>
              <input
                type="text"
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g. 85,000 USD"
              />
            </div>

            {/* Honeypot field — hidden from real users, visible to bots that fill every input */}
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`btn btn-primary submit-button ${isSubmitting ? 'submitting' : ''}`}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </button>

            {submitSuccess && (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <div>
                  <h3>Request Received!</h3>
                  <p>Thanks — our team will review your project and follow up by email with a quote.</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default RequestQuote;
